/**
 * Surface Context — the cross-surface coherence layer.
 *
 * The portal (browser), the desktop app, and the chrome extension are
 * three views of the same agent. Each surface has its own posture in
 * the workflow, but they share:
 *   - The same session (portal JWT)
 *   - The same agent (Pi)
 *   - The same ontology (MembraneLanguage + integration ontology)
 *   - The same register (work history, coverage, receipts)
 *
 * Surface roles:
 *   BROWSER    — the full workspace. All 17 views. The primary surface.
 *   DESKTOP    — focused work companion. Agent chat, terminal, notifications.
 *                The desktop is where the agent LIVES — always-on, ambient.
 *   EXTENSION  — the agent in the wild. Quick capture, context gathering,
 *                reply classification, lead intake. The agent on any page.
 *
 * The session protocol:
 *   1. User auths on any surface → portal JWT minted
 *   2. JWT is shared across surfaces via:
 *      - Browser: sessionStorage (same-origin)
 *      - Desktop: OS credential store via rida://connect deep link
 *      - Extension: chrome.storage.sync keyed to the portal domain
 *   3. Each surface declares its posture → Pi routes accordingly
 *   4. Actions on any surface receipt to the same register
 *
 * This is NOT a separate protocol — it IS the portal session, extended.
 */

export type SurfaceType = "browser" | "desktop" | "extension";

export interface SurfaceContext {
  surface: SurfaceType;
  /** Which workflow levels this surface participates in (not directly, but in parallel) */
  levels: WorkflowLevel[];
  /** What the user sees this surface AS (MembraneLanguage) */
  identity: string;
  /** The posture — how the agent presents on this surface */
  posture: SurfacePosture;
  /** Capabilities available on this surface */
  capabilities: SurfaceCapability[];
}

export type WorkflowLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface WorkflowLevelMeta {
  level: WorkflowLevel;
  /** Client-safe name (MembraneLanguage applied) */
  name: string;
  /** What happens at this level */
  description: string;
  /** Which surfaces participate */
  surfaces: SurfaceType[];
}

export type SurfacePosture = "full" | "ambient" | "capture";

export type SurfaceCapability =
  | "chat"           // talk to the agent
  | "terminal"       // command palette (⌘K)
  | "dashboard"      // status overview
  | "campaigns"      // outreach management
  | "offers"         // offer builder
  | "register"       // work history
  | "conveyance"     // agent handoff
  | "capture"        // quick context capture (extension)
  | "classify"       // reply classification (extension)
  | "notify"         // system notifications (desktop)
  | "profiles"       // subdomain profiles
  | "community"      // community access
  | "settings"       // full settings
  | "fleet";        // operator overview

// ── The 6-level workflow (bespoke at every level) ───────────────────────────
//
// These are the client-facing levels — NOT F-stages directly, but the
// client's own ontology mapped through MembraneLanguage. Each level
// has its own surfaces, its own capabilities, its own bespoke model.

export const WORKFLOW_LEVELS: WorkflowLevelMeta[] = [
  {
    level: 1,
    name: "Understand",
    description: "Your agent learns your business — the offer, the market, the voice.",
    surfaces: ["browser", "extension"],
  },
  {
    level: 2,
    name: "Build",
    description: "Your agent builds your offer, your campaigns, your sequences.",
    surfaces: ["browser", "desktop"],
  },
  {
    level: 3,
    name: "Deploy",
    description: "Your agent goes live — outreach starts, contacts flow in.",
    surfaces: ["browser", "desktop", "extension"],
  },
  {
    level: 4,
    name: "Work",
    description: "Your agent works — qualifying, advancing, closing. Receipts mint.",
    surfaces: ["browser", "desktop", "extension"],
  },
  {
    level: 5,
    name: "Prove",
    description: "Your agent's work is proven — coverage, receipts, causal spine.",
    surfaces: ["browser", "desktop"],
  },
  {
    level: 6,
    name: "Convey",
    description: "Your agent's work is conveyed — the sovereign book, your domain.",
    surfaces: ["browser"],
  },
];

// ── Surface definitions — bespoke per surface ──────────────────────────────

export const SURFACE_DEFINITIONS: Record<SurfaceType, SurfaceContext> = {
  browser: {
    surface: "browser",
    levels: [1, 2, 3, 4, 5, 6],
    identity: "Your workspace",
    posture: "full",
    capabilities: [
      "chat", "terminal", "dashboard", "campaigns", "offers",
      "register", "conveyance", "profiles", "community", "settings", "fleet",
    ],
  },
  desktop: {
    surface: "desktop",
    levels: [2, 3, 4, 5],
    identity: "Your agent",
    posture: "ambient",
    capabilities: ["chat", "terminal", "dashboard", "notify", "register"],
  },
  extension: {
    surface: "extension",
    levels: [1, 3, 4],
    identity: "Your agent in the wild",
    posture: "capture",
    capabilities: ["chat", "capture", "classify", "terminal"],
  },
};

// ── Surface detection ──────────────────────────────────────────────────────

export function detectSurface(): SurfaceType {
  // Check if running in the chrome extension context
  if (typeof chrome !== "undefined" && chrome.runtime && chrome.runtime.id) {
    return "extension";
  }
  // Check if running in the desktop app (Electron)
  if (typeof window !== "undefined" && window.__RIDA_DESKTOP__) {
    return "desktop";
  }
  // Default: browser
  return "browser";
}

// ── Session sharing protocol ───────────────────────────────────────────────

export interface SurfaceSession {
  jwt: string;
  surface: SurfaceType;
  tenant_id: string | null;
  role: "client" | "principal";
  expires_at?: number;
}

/**
 * Get the session for the current surface.
 * Each surface has its own storage mechanism but the JWT is the same.
 */
export function getSurfaceSession(): SurfaceSession | null {
  const jwt = sessionStorage.getItem("rida_workspace_jwt");
  if (!jwt) return null;

  const surface = detectSurface();
  const expiry = sessionStorage.getItem("rida_workspace_jwt_expiry");

  return {
    jwt,
    surface,
    tenant_id: null, // resolved server-side from JWT
    role: "client",  // resolved server-side from JWT
    expires_at: expiry ? Number(expiry) : undefined,
  };
}

/**
 * Share the session to the desktop app via deep link.
 * The desktop app registers the rida:// protocol handler.
 */
export function shareToDesktop(): string {
  const session = getSurfaceSession();
  if (!session) return "";
  // The desktop app opens rida://connect with the JWT as a one-time code.
  // The control-server exchanges the code for a desktop-specific session.
  return `rida://connect?jwt=${encodeURIComponent(session.jwt)}&surface=desktop`;
}

/**
 * Share the session to the chrome extension.
 * The extension uses chrome.storage.sync keyed to the portal domain.
 */
export function shareToExtension(): void {
  const session = getSurfaceSession();
  if (!session) return;
  // In browser context, we can use window.postMessage to communicate
  // with a content script that the extension has injected.
  window.postMessage({
    type: "rida:session",
    jwt: session.jwt,
    surface: "extension",
  }, "*");
}

// ── Surface-aware navigation ───────────────────────────────────────────────

/**
 * Get the capabilities available on the current surface.
 * This drives which views are visible and which terminal commands are active.
 */
export function getSurfaceCapabilities(): SurfaceCapability[] {
  const surface = detectSurface();
  return SURFACE_DEFINITIONS[surface].capabilities;
}

/**
 * Check if a capability is available on the current surface.
 */
export function hasCapability(cap: SurfaceCapability): boolean {
  return getSurfaceCapabilities().includes(cap);
}

/**
 * Get the workflow levels active on the current surface.
 */
export function getSurfaceLevels(): WorkflowLevelMeta[] {
  const surface = detectSurface();
  const def = SURFACE_DEFINITIONS[surface];
  return WORKFLOW_LEVELS.filter(l => def.levels.includes(l.level));
}

// ── Pi routing — surface-aware agent dispatch ─────────────────────────────

export interface PiRouteRequest {
  message: string;
  surface: SurfaceType;
  /** The page context (extension captures the current page) */
  pageContext?: {
    url: string;
    title: string;
    selection?: string;
  };
}

export interface PiRouteResponse {
  reply: string;
  receipt_id?: string;
  capability_used?: string;
  level?: WorkflowLevel;
}

/**
 * Build a Pi routing envelope. The agent (Pi) receives the surface context
 * and routes through its capability registry accordingly.
 *
 * On the browser: full workspace — all capabilities available.
 * On the desktop: ambient — focused on chat, notifications, register.
 * On the extension: capture — quick actions, context gathering, classification.
 */
export function buildPiEnvelope(message: string, pageContext?: PiRouteRequest["pageContext"]): PiRouteRequest {
  return {
    message,
    surface: detectSurface(),
    pageContext,
  };
}
